import { X } from 'lucide-react'
import React from 'react'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: React.ReactNode
  description?: React.ReactNode
  footer?: React.ReactNode
  closeLabel?: string
  width?: number
  children: React.ReactNode
}

/**
 * Native-dialog slide-over shell. Its top-layer animation and backdrop remain
 * in the admin-only `.adm-drawer` CSS because those browser primitives cannot
 * be expressed reliably with utilities.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  closeLabel = '關閉',
  width = 520,
  children,
}: DrawerProps) {
  const dialogRef = React.useRef<HTMLDialogElement>(null)
  const triggerRef = React.useRef<HTMLElement | null>(null)
  const titleId = React.useId()

  React.useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      const active = document.activeElement
      triggerRef.current = active instanceof HTMLElement && active !== document.body ? active : null
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal()
        else dialog.setAttribute('open', '')
      }
      return
    }

    if (dialog.open) {
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
    triggerRef.current?.focus()
  }, [open])

  return (
    <dialog
      ref={dialogRef}
      className="adm-drawer"
      aria-labelledby={titleId}
      style={{ width }}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <header className="flex items-start gap-3 border-b border-border px-[22px] py-[18px]">
        <div>
          <h2 id={titleId} className="text-[length:var(--text-xl)]">{title}</h2>
          {description ? <p className="mt-[3px] text-xs-plus text-muted">{description}</p> : null}
        </div>
        <button
          type="button"
          className="ml-auto inline-flex size-[30px] items-center justify-center rounded-[var(--radius)] text-muted transition-colors hover:bg-surface hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)] focus-visible:outline-offset-2"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </header>
      <div className="flex-1 overflow-auto p-[22px]">{children}</div>
      {footer ? <footer className="flex items-center gap-2.5 border-t border-border bg-background px-[22px] py-4">{footer}</footer> : null}
    </dialog>
  )
}
